import {
  Alert as MuiAlert,
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { CheckCircle, QueryStats, Terminal, Webhook } from '@mui/icons-material';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { getApiConsumption, listApiLogs } from '@/api/apiLogs';
import { listWebhookDeliveries } from '@/api/webhooks';
import { toApiError } from '@/api/client';
import { LoadingScreen } from '@/components/LoadingScreen';
import { formatDateTime } from '@/lib/formatters';

export const Route = createFileRoute('/_authenticated/dashboard/api')({
  component: DashboardApiPage,
});

function statusColor(code: number): 'success' | 'warning' | 'error' | 'default' {
  if (code >= 500) return 'error';
  if (code >= 400) return 'warning';
  if (code >= 200 && code < 300) return 'success';
  return 'default';
}

function DashboardApiPage(): JSX.Element {
  const consQ = useQuery({ queryKey: ['api-consumption'], queryFn: () => getApiConsumption() });
  const logsQ = useQuery({ queryKey: ['api-logs', '', '', ''], queryFn: () => listApiLogs({}, 0, 8) });
  const failedQ = useQuery({ queryKey: ['webhook-deliveries', 'Failed'], queryFn: () => listWebhookDeliveries('Failed', 0, 1) });
  const deadQ = useQuery({ queryKey: ['webhook-deliveries', 'DeadLettered'], queryFn: () => listWebhookDeliveries('DeadLettered', 0, 1) });

  if (consQ.isLoading) return <LoadingScreen message="Cargando panel de API…" />;

  const endpoints = consQ.data?.endpoints ?? [];
  const totalCalls = endpoints.reduce((n, e) => n + e.count, 0);
  const totalOk = endpoints.reduce((n, e) => n + e.successCount, 0);
  const successPct = totalCalls > 0 ? Math.round((totalOk / totalCalls) * 100) : 100;
  const logsUnavailable = Boolean(logsQ.error);
  const webhooksUnavailable = Boolean(failedQ.error || deadQ.error);
  const degraded = logsUnavailable || webhooksUnavailable;
  const webhookIssues = webhooksUnavailable ? null : (failedQ.data?.total ?? 0) + (deadQ.data?.total ?? 0);
  const recent = logsQ.data?.items ?? [];

  return (
    <Box>
      <Stack sx={{ mb: 3 }}>
        <Typography variant="h4">Resumen de tu integración</Typography>
        <Typography variant="body2" color="text.secondary">Salud de tu integración: consumo, últimas llamadas y webhooks.</Typography>
      </Stack>

      {consQ.error && <MuiAlert severity="error" sx={{ mb: 2 }}>{toApiError(consQ.error).message}</MuiAlert>}
      {degraded && (
        <MuiAlert severity="warning" sx={{ mb: 2 }}>
          Estamos mostrando datos parciales:
          {logsUnavailable ? ' no se pudieron cargar las últimas llamadas.' : ''}
          {webhooksUnavailable ? ' no se pudo consultar el estado de webhooks.' : ''}
        </MuiAlert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Kpi label="Operaciones (30 días)" value={totalCalls.toLocaleString('es-AR')} icon={<QueryStats color="primary" />} />
        <Kpi label="Tasa de éxito" value={`${successPct}%`} icon={<CheckCircle color={successPct >= 99 ? 'success' : successPct >= 90 ? 'warning' : 'error'} />} />
        <Kpi label="Endpoints usados" value={String(endpoints.length)} icon={<Terminal color="action" />} />
        <Kpi
          label="Webhooks con problema"
          value={webhookIssues === null ? 'No disponible' : String(webhookIssues)}
          icon={<Webhook color={webhookIssues === null ? 'warning' : webhookIssues > 0 ? 'error' : 'action'} />}
        />
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>Últimas llamadas</Typography>
              {logsUnavailable ? (
                <MuiAlert severity="warning">No pudimos cargar las últimas llamadas.</MuiAlert>
              ) : recent.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Sin llamadas registradas.</Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Método</TableCell>
                      <TableCell>Endpoint</TableCell>
                      <TableCell align="right">Estado</TableCell>
                      <TableCell align="right">Latencia</TableCell>
                      <TableCell>Fecha</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recent.map((l) => (
                      <TableRow key={l.id} hover>
                        <TableCell><Chip size="small" variant="outlined" label={l.method} /></TableCell>
                        <TableCell><Typography variant="caption" sx={{ fontFamily: 'var(--font-mono)' }}>{l.endpoint}</Typography></TableCell>
                        <TableCell align="right"><Chip size="small" variant="outlined" color={statusColor(l.statusCode)} label={l.statusCode} /></TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{l.latencyMs} ms</TableCell>
                        <TableCell><Typography variant="caption" color="text.secondary">{formatDateTime(l.occurredAt)}</Typography></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={5}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>Top endpoints (30 días)</Typography>
              {endpoints.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Sin actividad.</Typography>
              ) : (
                <Stack spacing={1}>
                  {[...endpoints].sort((a, b) => b.count - a.count).slice(0, 6).map((e) => (
                    <Paper key={e.endpoint} variant="outlined" sx={{ p: 1 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" sx={{ fontFamily: 'var(--font-mono)' }} noWrap>{e.endpoint}</Typography>
                        <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{e.count.toLocaleString('es-AR')}</Typography>
                      </Stack>
                      <Typography variant="caption" color="text.secondary">{Math.round(e.successRate * 100)}% éxito · p99 {e.p99LatencyMs} ms</Typography>
                    </Paper>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

function Kpi({ label, value, icon }: { label: string; value: string; icon: JSX.Element }): JSX.Element {
  return (
    <Grid item xs={6} md={3}>
      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" spacing={1.5} alignItems="center">
            {icon}
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h5" noWrap>{value}</Typography>
              <Typography variant="caption" color="text.secondary">{label}</Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Grid>
  );
}
